import { normalizeForMatch } from "../lib/textNormalize";

// 読み上げ中に入った発話が「本当の回答」かを端末側だけで即断する (spec 2026-07-28 §3.4)。
//
// この関数は「回答である」とは判定しない。明らかな非回答を救い出すだけで、
// 残りはすべて unsure として下流 (構造化 LLM) に委ねる。
//
// 誤判定の非対称性が設計の中心にある:
//   誤って回答を弾く → ユーザーの発話が吹き出しも出ずに消える (今回直す症状の再発)
//   回り込みを見逃す → AI の声が回答になる (現状の挙動。悪化しない)
// したがって閾値はすべて「弾かない側」に倒してある。

export type InterruptionReason = "empty" | "loopback" | "backchannel";

export type InterruptionVerdict = {
  verdict: "not-answer" | "unsure";
  reason?: InterruptionReason;
};

export type InterruptionContext = {
  /** いま読み上げている本文 (SSML タグを含まない表示テキスト)。 */
  narratingText: string;
  /**
   * 期待する答えが読み上げ文に逐語で引用されている場面か (spec D12)。
   * 送信確認 (「『はい』または『修正する』とお話しください」) と続きから確認では、
   * 「はい」がユーザー由来か AI 由来かをテキストだけでは原理的に判別できない。
   *
   * **降ろすのは相槌ルールだけ** (2026-07-28 のレビューで訂正)。当初は回り込み判定も
   * 降ろしていたが、実測すると引用された本物の回答はすべて 4 文字以下か編集距離 0.375 以上で、
   * 回り込み判定に掛けても 1 件も弾かれない。一方この 2 phase は AI 自身の声が回り込むと
   * 被害が最も大きい — 「最初からになさいますか」がそのまま restart 意図として通ると
   * `handleStartOver` が走り、**確認なしに回答が全部消える**。降ろす範囲を相槌だけに絞る。
   */
  quotedAnswers: boolean;
};

// 読み上げ文の中で発話に最も近い窓との編集距離が、発話長のこの割合以下なら回り込みとみなす。
// 実測 (spec §3.4.2 の表): 回り込み 0.000〜0.059 / 本物の回答 0.182〜0.800。
const LOOPBACK_MAX_DISTANCE = 0.1;

// これより短い発話には回り込み判定を掛けない。
//
// **6 文字では短すぎた** (2026-07-28 のレビューで実測して訂正)。読み上げ文の
// **任意の連続部分文字列は編集距離 0.000 になる**ので、質問文の語幹を借りて答えると
// 本物の回答が弾かれる。日本語では「困ったことはありますか?」→「困ったことはあります」の
// ように語幹を借りて答えるのが自然で、しかも `long` の発話終了判定が 1800ms あるため
// 「印象に残ったことは…」と言いかけて考えると語幹だけが確定する。
//
// 実測 (本物の回答 25 件 / 回り込み 14 件):
//   6 文字 → 本物の回答を 9 件も破棄 (「困ったことはあります」など)。**症状の再生産**
//  14 文字 → 破棄 0 件 / 回り込みの見逃し 1 件 (「最初からになさいますか」11 文字)
// 見逃した短い回り込みは `unsure` として構造化 LLM に回り、その出力にもう一度
// 同じ判定を当てる (事後照合) ので、完全に素通しにはならない。
const LOOPBACK_MIN_LENGTH = 14;

// 相槌・言い淀み。yesNoIntent の FILLERS とは意図的に別に持つ (spec §3.4.5)。
// あちらは「『ええと』の『ええ』が肯定と誤判定されるのを防ぐ」ために選ばれた語彙で、
// 事前チェックの wizard から出る唯一の経路がそこに乗っている。こちらの都合で語を足すと
// あちらの判定が静かに変わるため、7 行の重複を選ぶ。
const FILLERS = ["ええと", "えーと", "えっと", "うんと", "あのー", "あの", "まあ"];

// 発話全体がこれと完全一致したときだけ相槌とみなす。部分一致にすると
// 「そうですね、今週は資料を作っていて…」のような本物の回答を、先頭の相槌だけで弾く。
//
// 「そうですね」は入れない: 日本語では相槌ではなく回答の前置きが支配的で、
// long の発話終了判定 (1800ms) で切られると「答えを組み立て始めた瞬間に
// AI が喋り出す」ことになる。分類は正しくても帰結 (再開) が間違っている。
// 「あ」は入れない: 1 文字なので空判定が先に捕まえる。ここに置くと計測の内訳が歪む。
const BACKCHANNELS = [
  "うん",
  "うんうん",
  "ええ",
  "はい",
  "へー",
  "ほー",
  "なるほど",
  "わかりました",
];

function stripFillers(s: string): string {
  let result = s;
  for (const filler of FILLERS) {
    result = result.split(filler).join("");
  }
  return result;
}

/**
 * 近似部分文字列マッチ (Sellers)。text の中で pattern に最も近い窓を探し、
 * その編集距離を pattern の長さで割って返す。開始位置を自由にした編集距離 DP。
 *
 * 回り込みは定義上「読み上げ文の連続した断片」で、STT の誤りが乗っても連続性は保たれる。
 * 一方、本物の回答が読み上げ文と共有するのは語尾の文法だけで連続しない
 * (設問がすべて「〜しましたか」型なので、回答も「〜しました」で終わる)。
 *
 * 文字 bigram の被覆率ではこの 2 つを分離できない。被覆率は語順も連続性も見ないため、
 * 内容語を 1 つも共有していない「採用面接に取り組みました」が質問文と 0.636 も
 * 一致してしまう (spec §3.4.2)。
 */
function approxSubstringDistance(pattern: string, text: string): number {
  if (pattern.length === 0) return 0;
  // prev[j] = pattern の先頭 i 文字と、text の「どこかから始まって j で終わる窓」との
  // 最小編集距離。開始位置が自由なので 0 行目はすべて 0。
  let prev = new Array<number>(text.length + 1).fill(0);
  for (let i = 1; i <= pattern.length; i++) {
    const cur = new Array<number>(text.length + 1);
    cur[0] = i;
    for (let j = 1; j <= text.length; j++) {
      cur[j] = Math.min(
        prev[j] + 1,
        cur[j - 1] + 1,
        prev[j - 1] + (pattern[i - 1] === text[j - 1] ? 0 : 1)
      );
    }
    prev = cur;
  }
  return Math.min(...prev) / pattern.length;
}

export function judgeInterruption(
  transcript: string,
  ctx: InterruptionContext
): InterruptionVerdict {
  const t = normalizeForMatch(transcript);
  // 空だけを弾く。1 文字も弾いていたが (2026-07-28 のレビューで訂正)、数字ひとつ
  // (`scale` 設問の正規の回答) や「無」「有」のような短い回答まで捨てていた。
  // 咳や物音は Scribe が空文字を返すのでこれで足りる。
  if (t.length === 0) return { verdict: "not-answer", reason: "empty" };

  const withoutFillers = stripFillers(t);
  // フィラーだけの発話。ここで拾わないと LLM 往復に落ちる。
  if (withoutFillers.length === 0) return { verdict: "not-answer", reason: "empty" };

  // 回り込み判定は確認の場面でも効かせる。引用された本物の回答 (「はい」「続きから」
  // 「最初からにします」) は短いか編集距離が十分に離れているので弾かれない (実測済)。
  if (t.length >= LOOPBACK_MIN_LENGTH) {
    const narrating = normalizeForMatch(ctx.narratingText);
    if (narrating.length > 0 && approxSubstringDistance(t, narrating) <= LOOPBACK_MAX_DISTANCE) {
      return { verdict: "not-answer", reason: "loopback" };
    }
  }

  // 相槌ルールだけは確認の場面で降ろす (D12)。ここでは「はい」が本物の回答であり、
  // ユーザー由来か AI 由来かをテキストだけでは原理的に判別できない。弾くと
  // 声だけで送信できなくなる。
  if (!ctx.quotedAnswers && BACKCHANNELS.includes(withoutFillers)) {
    return { verdict: "not-answer", reason: "backchannel" };
  }

  return { verdict: "unsure" };
}
